import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface MeasurementUpdate {
  type: string;
  data: any;
  timestamp: Date;
}

interface KpiUpdate {
  data: any;
  timestamp: Date;
}

interface AlertNotification {
  alert: any;
  timestamp: Date;
}

interface DigesterStatus {
  data: any;
  timestamp: Date;
}

interface SystemStatus {
  status: any;
  timestamp: Date;
}

interface WebSocketContextType {
  socket: Socket | null;
  connected: boolean;
  latestMeasurement: MeasurementUpdate | null;
  latestKpi: KpiUpdate | null;
  latestAlert: AlertNotification | null;
  latestDigesterStatus: DigesterStatus | null;
  systemStatus: SystemStatus | null;
}

const WebSocketContext = createContext<WebSocketContextType>({
  socket: null,
  connected: false,
  latestMeasurement: null,
  latestKpi: null,
  latestAlert: null,
  latestDigesterStatus: null,
  systemStatus: null,
});

export const useWebSocket = () => useContext(WebSocketContext);

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [latestMeasurement, setLatestMeasurement] = useState<MeasurementUpdate | null>(null);
  const [latestKpi, setLatestKpi] = useState<KpiUpdate | null>(null);
  const [latestAlert, setLatestAlert] = useState<AlertNotification | null>(null);
  const [latestDigesterStatus, setLatestDigesterStatus] = useState<DigesterStatus | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);

  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4001';
    
    const newSocket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 2000,
      reconnectionAttempts: 3,
      timeout: 3000,
    });

    newSocket.on('connect', () => {
      console.log('WebSocket connected');
      setConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });

    // Listen for measurement updates
    newSocket.on('measurement:update', (data: MeasurementUpdate) => {
      console.log('Received measurement update:', data.type);
      setLatestMeasurement(data);
    });

    // Listen for KPI updates
    newSocket.on('kpi:update', (data: KpiUpdate) => {
      console.log('Received KPI update');
      setLatestKpi(data);
    });

    // Listen for alert notifications
    newSocket.on('alert:new', (data: AlertNotification) => {
      console.log('Received new alert:', data.alert.alert_type);
      setLatestAlert(data);
    });

    // Listen for digester status updates
    newSocket.on('digester:status', (data: DigesterStatus) => {
      console.log('Received digester status update');
      setLatestDigesterStatus(data);
    });

    // Listen for system status
    newSocket.on('system:status', (data: SystemStatus) => {
      console.log('Received system status update');
      setSystemStatus(data);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const value: WebSocketContextType = {
    socket,
    connected,
    latestMeasurement,
    latestKpi,
    latestAlert,
    latestDigesterStatus,
    systemStatus,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
